import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Building2, Clock, FlaskConical, Lock, Factory, Megaphone } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { Lead } from './types'

function getInitials(lead: Lead): string {
  const first = lead.first_name?.[0] ?? ''
  const last = lead.last_name?.[0] ?? ''
  return (first + last).toUpperCase() || '?'
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

type LeadCardProps = {
  readonly lead: Lead
  readonly onLeadClick: (lead: Lead) => void
  readonly selectMode?: boolean
  readonly isSelected?: boolean
  readonly onToggleSelect?: (id: string) => void
  /**
   * Stage transitions are engine-driven; the board only permits dragging
   * meeting_booked cards (to Won/Lost). Everything else renders without a
   * drag handle so the affordance matches the policy.
   */
  readonly dragDisabled?: boolean
}

export function LeadCard({ lead, onLeadClick, selectMode = false, isSelected = false, onToggleSelect, dragDisabled = false }: LeadCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id,
    // Disable drag while in select mode so the card click reliably toggles
    // selection instead of triggering a drag start.
    disabled: selectMode || dragDisabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const displayName = lead.full_name ?? `${lead.first_name} ${lead.last_name}`
  const isEnriched = Boolean(lead.enriched_at)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-md cursor-pointer',
        isDragging && 'opacity-50 shadow-lg ring-2 ring-ring',
        selectMode && isSelected && 'ring-2 ring-[var(--color-cobalt)] bg-[var(--color-cobalt)]/5',
      )}
      onClick={() => onLeadClick(lead)}
    >
      {/* Selection checkbox — replaces the drag handle in select mode. */}
      {selectMode ? (
        <div
          className="absolute right-2 top-2"
          onClick={(e) => {
            e.stopPropagation()
            onToggleSelect?.(lead.id)
          }}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect?.(lead.id)}
            aria-label={`Select ${displayName}`}
          />
        </div>
      ) : dragDisabled ? null : (
        <button
          type="button"
          className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to Won or Lost"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <div className="flex items-start gap-2.5 pr-5">
        <Avatar className="h-8 w-8 shrink-0">
          {lead.photo_url && <AvatarImage src={lead.photo_url} alt={displayName} />}
          <AvatarFallback className="text-xs">{getInitials(lead)}</AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-tight">{displayName}</p>
          {lead.company ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{lead.company}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-xs text-subtle-foreground">
              <Lock className="h-3 w-3 shrink-0" />
              <span>Company (enrich to unlock)</span>
            </div>
          )}
          {lead.job_title && (
            <p className="truncate text-xs text-muted-foreground">{lead.job_title}</p>
          )}
        </div>
      </div>

      {/* Enrichment-dependent fields + campaign membership */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        {lead.campaign && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-[rgba(28,63,255,0.10)] px-2 py-0.5 text-[var(--color-cobalt-bright)]"
            title={`In campaign: ${lead.campaign}`}
          >
            <Megaphone className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[110px]">{lead.campaign}</span>
          </span>
        )}
        {lead.industry ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
            <Factory className="h-3 w-3" />
            <span className="truncate max-w-[120px]">{lead.industry}</span>
          </span>
        ) : !isEnriched ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-subtle-foreground">
            <Lock className="h-3 w-3" />
            Industry
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <span title={isEnriched ? 'Researched' : 'Not researched. Click to enrich.'}>
          <FlaskConical className={cn('h-3.5 w-3.5', isEnriched ? 'text-emerald-300' : 'text-subtle-foreground')} />
        </span>

        {lead.last_activity_at && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{relativeTime(lead.last_activity_at)}</span>
          </div>
        )}
      </div>
    </div>
  )
}
